import { useCallback, useEffect, useRef, useState } from "react";

const ESPERAS_MS = [3_000, 20_000];

/**
 * Reintentos de la foto de un avatar. Radix queda en "error" hasta que cambia `src`, así que un
 * fallo pasajero (red caída, equipo en reposo, CDN) dejaba las iniciales hasta recargar. Aquí
 * `intento` sube (se usa como `key` de la imagen para remontarla) con espera corta, y también al
 * volver la red o la pestaña. Se rinde tras unos intentos para no martillar una URL que da 404.
 */
export function useFotoReintento(src: string): { intento: number; alEstado: (estado: string) => void } {
  const [intento, setIntento] = useState(0);
  const fallosRef = useRef(0);
  const falloActivoRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    fallosRef.current = 0;
    falloActivoRef.current = false;
    setIntento(0);
    return () => clearTimeout(timerRef.current);
  }, [src]);

  useEffect(() => {
    const reintentar = () => {
      if (!falloActivoRef.current || document.visibilityState === "hidden") return;
      falloActivoRef.current = false;
      clearTimeout(timerRef.current);
      setIntento((n) => n + 1);
    };
    window.addEventListener("online", reintentar);
    document.addEventListener("visibilitychange", reintentar);
    return () => {
      window.removeEventListener("online", reintentar);
      document.removeEventListener("visibilitychange", reintentar);
    };
  }, []);

  const alEstado = useCallback((estado: string) => {
    if (estado === "loaded") {
      fallosRef.current = 0;
      falloActivoRef.current = false;
      return;
    }
    if (estado !== "error") return;
    falloActivoRef.current = true;
    const espera = ESPERAS_MS[fallosRef.current];
    fallosRef.current += 1;
    if (espera === undefined) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      falloActivoRef.current = false;
      setIntento((n) => n + 1);
    }, espera);
  }, []);

  return { intento, alEstado };
}
